import { LoaderCircle } from 'lucide-react';
import { createContext, type ReactNode, useCallback, useContext, useMemo, useState } from 'react';
import { errorMessage } from '../../lib/utils';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../ui/alert-dialog';
import { buttonVariants } from '../ui/button';

export interface ConfirmOptions {
  title: string;
  description?: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  /** Red confirm button for deletes/archives. */
  destructive?: boolean;
  /**
   * Runs while the dialog stays open with a spinner; a thrown error is shown inline and the
   * dialog stays open. Without it `confirm()` resolves as soon as the user answers.
   */
  onConfirm?: () => Promise<unknown> | unknown;
}

type ConfirmFn = (options: ConfirmOptions) => Promise<boolean>;

const ConfirmContext = createContext<ConfirmFn | null>(null);

/** `const confirm = useConfirm(); if (await confirm({ title: 'Delete?' , destructive: true })) …` */
export function useConfirm(): ConfirmFn {
  const fn = useContext(ConfirmContext);
  if (fn === null) throw new Error('useConfirm() must be called inside <ConfirmProvider>');
  return fn;
}

interface Pending {
  options: ConfirmOptions;
  resolve: (value: boolean) => void;
}

/** Mount once (the app shell does). Hosts the single confirm dialog for the whole app. */
export function ConfirmProvider({ children }: { children: ReactNode }) {
  const [pending, setPending] = useState<Pending | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const confirm = useCallback<ConfirmFn>(
    (options) =>
      new Promise<boolean>((resolve) => {
        setError(null);
        setPending({ options, resolve });
      }),
    [],
  );

  const close = (value: boolean) => {
    pending?.resolve(value);
    setPending(null);
    setBusy(false);
    setError(null);
  };

  const onConfirm = async () => {
    if (!pending) return;
    if (!pending.options.onConfirm) {
      close(true);
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await pending.options.onConfirm();
      close(true);
    } catch (e) {
      setBusy(false);
      setError(errorMessage(e));
    }
  };

  const value = useMemo(() => confirm, [confirm]);
  const options = pending?.options;

  return (
    <ConfirmContext.Provider value={value}>
      {children}
      <AlertDialog
        open={pending !== null}
        onOpenChange={(open) => (open ? undefined : close(false))}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{options?.title}</AlertDialogTitle>
            {options?.description ? (
              <AlertDialogDescription>{options.description}</AlertDialogDescription>
            ) : null}
          </AlertDialogHeader>
          {error ? (
            <p role="alert" className="rounded-md bg-danger-soft p-2 text-danger text-sm">
              {error}
            </p>
          ) : null}
          <AlertDialogFooter>
            <AlertDialogCancel disabled={busy} onClick={() => close(false)}>
              {options?.cancelLabel ?? 'Cancel'}
            </AlertDialogCancel>
            <AlertDialogAction
              className={
                options?.destructive ? buttonVariants({ variant: 'destructive' }) : undefined
              }
              disabled={busy}
              onClick={(event) => {
                event.preventDefault();
                void onConfirm();
              }}
            >
              {busy ? <LoaderCircle className="size-4 animate-spin" /> : null}
              {options?.confirmLabel ?? 'Confirm'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </ConfirmContext.Provider>
  );
}
